/* MediaPicker - shared component for uploading and selecting images from the
 * media pool. Supports single and multi (gallery) modes.
 *
 * Usage:
 *   <MediaPicker value={url} onChange={(url) => ...} />            // single
 *   <MediaPicker multi value={[url1, url2]} onChange={(urls) => ...} />  // multi
 */
import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Upload, Image as ImageIcon, X, FolderOpen, Check, Trash2, Link as LinkIcon } from "lucide-react";

function absUrl(u) {
  if (!u) return "";
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  // backend-served media: /api/media/file/xyz.jpg
  const base = process.env.REACT_APP_BACKEND_URL || "";
  if (u.startsWith("/api/")) return `${base}${u}`;
  if (u.startsWith("/")) return `${base}${u}`;
  return u;
}

export default function MediaPicker({
  value,
  onChange,
  multi = false,
  label,
  scope = "shared",
  province_id = null,
  testId,
}) {
  const [poolOpen, setPoolOpen] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef();

  // Normalize value to array internally
  const list = multi ? (Array.isArray(value) ? value : []) : value ? [value] : [];

  const setList = (next) => {
    if (multi) onChange(next);
    else onChange(next[0] || "");
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const newUrls = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("scope", scope);
        if (province_id) fd.append("province_id", province_id);
        const r = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        newUrls.push(r.data.url);
        if (!multi) break; // single mode → only one
      }
      if (multi) setList([...list, ...newUrls]);
      else setList(newUrls);
      toast.success(`${newUrls.length} dosya yüklendi (havuza eklendi)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Yükleme başarısız");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeAt = (idx) => {
    const next = [...list];
    next.splice(idx, 1);
    setList(next);
  };

  const addUrl = () => {
    if (!urlInput.trim()) return;
    if (multi) setList([...list, urlInput.trim()]);
    else setList([urlInput.trim()]);
    setUrlInput("");
  };

  return (
    <div data-testid={testId || "media-picker"}>
      {label && <label className="block text-xs font-medium mb-1 text-gray-600">{label}</label>}

      {/* Preview grid */}
      {list.length > 0 && (
        <div className={`grid ${multi ? "grid-cols-3 sm:grid-cols-4" : "grid-cols-1"} gap-2 mb-2`}>
          {list.map((u, i) => (
            <div key={`${u}-${i}`} className="relative group border rounded-md overflow-hidden bg-gray-50">
              <img src={absUrl(u)} alt="" className={`w-full ${multi ? "aspect-square" : "aspect-video"} object-cover`} />
              <button
                type="button"
                onClick={() => removeAt(i)}
                className="absolute top-1 right-1 p-1 bg-black/60 text-white rounded hover:bg-red-600"
                data-testid={`${testId || "media-picker"}-remove-${i}`}
                title="Kaldır"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Actions row */}
      <div className="flex gap-2 flex-wrap">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple={multi}
          className="hidden"
          onChange={handleUpload}
        />
        <button
          type="button"
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
          className="px-3 py-1.5 text-xs font-semibold bg-[#e11d2a] text-white rounded flex items-center gap-1 disabled:opacity-50"
          data-testid={`${testId || "media-picker"}-upload-btn`}
        >
          <Upload className="w-3.5 h-3.5" /> {uploading ? "Yükleniyor..." : multi ? "Foto Yükle (çoklu)" : "Foto Yükle"}
        </button>
        <button
          type="button"
          onClick={() => setPoolOpen(true)}
          className="px-3 py-1.5 text-xs font-semibold border rounded flex items-center gap-1"
          data-testid={`${testId || "media-picker"}-pool-btn`}
        >
          <FolderOpen className="w-3.5 h-3.5" /> Havuzdan Seç
        </button>
        <div className="flex items-center gap-1 flex-1 min-w-[200px]">
          <LinkIcon className="w-3.5 h-3.5 text-gray-400 shrink-0" />
          <input
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder="veya URL yapıştır"
            className="flex-1 px-2 py-1.5 border rounded text-xs"
          />
          <button
            type="button"
            onClick={addUrl}
            className="px-2 py-1.5 bg-gray-100 text-xs rounded hover:bg-gray-200"
            disabled={!urlInput.trim()}
          >
            Ekle
          </button>
        </div>
      </div>

      {poolOpen && (
        <MediaPoolModal
          multi={multi}
          onClose={() => setPoolOpen(false)}
          onPick={(urls) => {
            if (multi) setList([...list, ...urls]);
            else setList([urls[0]]);
            setPoolOpen(false);
          }}
        />
      )}
    </div>
  );
}

function MediaPoolModal({ multi, onClose, onPick }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);

  const PAGE_SIZE = 60;

  const run = async (append = false, query = search) => {
    setLoading(true);
    try {
      const skip = append ? items.length : 0;
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        skip: String(skip),
      });
      if (query) params.set("search", query);
      const r = await api.get(`/media?${params}`);
      const next = r.data.items || [];
      setItems((prev) => (append ? [...prev, ...next] : next));
      setTotal(r.data.total || 0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancel = false;
    const t = setTimeout(() => { if (!cancel) run(false, search); }, 300);
    return () => { cancel = true; clearTimeout(t); };
    // eslint-disable-next-line
  }, [search]);

  const toggle = (url) => {
    if (multi) {
      setSelected((s) => (s.includes(url) ? s.filter((x) => x !== url) : [...s, url]));
    } else {
      onPick([url]);
    }
  };

  const hasMore = items.length < total;

  return (
    <div
      className="fixed inset-0 bg-black/60 z-[200] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="media-pool-modal"
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b flex items-center justify-between gap-2">
          <h3 className="font-bold flex items-center gap-1"><ImageIcon className="w-4 h-4" /> Medya Havuzu</h3>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Ara..."
            className="px-3 py-1.5 border rounded text-sm flex-1 max-w-xs"
          />
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 flex-1 overflow-y-auto">
          {loading && items.length === 0 ? (
            <div className="text-center text-gray-500 py-10">Yükleniyor...</div>
          ) : items.length === 0 ? (
            <div className="text-center text-gray-500 py-10">Havuz boş — önce dosya yükleyin.</div>
          ) : (
            <>
              <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                {items.map((m) => {
                  const isSel = selected.includes(m.url);
                  return (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => toggle(m.url)}
                      className={`relative aspect-square border rounded overflow-hidden bg-gray-50 hover:border-[#e11d2a] ${
                        isSel ? "ring-2 ring-[#e11d2a]" : ""
                      }`}
                      title={m.title || m.filename}
                    >
                      <img src={absUrl(m.url)} alt={m.title || m.filename} className="w-full h-full object-cover" />
                      {isSel && (
                        <span className="absolute top-1 right-1 bg-[#e11d2a] text-white rounded-full p-0.5">
                          <Check className="w-3 h-3" />
                        </span>
                      )}
                      <span className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent text-[10px] text-white px-1 py-0.5 truncate">
                        {m.title || m.filename}
                      </span>
                    </button>
                  );
                })}
              </div>
              {hasMore && (
                <div className="flex justify-center mt-4">
                  <button
                    type="button"
                    onClick={() => run(true)}
                    disabled={loading}
                    data-testid="media-pool-load-more"
                    className="px-4 py-1.5 text-xs font-semibold bg-white border border-gray-200 hover:bg-gray-50 rounded-md disabled:opacity-50"
                  >
                    {loading ? "Yükleniyor..." : `Daha Fazla Yükle (${items.length} / ${total})`}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
        {multi && (
          <div className="p-3 border-t flex items-center justify-between">
            <div className="text-xs text-gray-500">{selected.length} seçili</div>
            <div className="flex gap-2">
              <button onClick={onClose} className="px-3 py-1.5 text-sm border rounded">İptal</button>
              <button
                onClick={() => onPick(selected)}
                disabled={!selected.length}
                className="px-4 py-1.5 text-sm bg-[#e11d2a] text-white rounded disabled:opacity-50"
              >
                Seçilenleri Ekle ({selected.length})
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
